import { NodeViewWrapper, type NodeViewProps } from "@tiptap/react";
import type { ReactNode } from "react";
import { Icon } from "../../icons";
import { localized } from "../../localization";

/** The attributes a `videoBlock` node carries in the document JSON. */
export interface VideoBlockAttrs {
	videoId: string | null;
}

/**
 * Renders the embedded player for one video id. The editor package owns no API client, so
 * the app supplies this — a component that resolves the id to signed playback tokens.
 * Without one the block renders as a labelled placeholder, which is what every call site
 * that never embeds videos gets for free. `isEditable` says whether the hosting editor
 * accepts changes, which is where a renderer decides to offer editing actions like trimming.
 */
export type VideoRenderer = (attrs: { videoId: string; isEditable?: boolean }) => ReactNode;

/** Props Interface */
interface VideoBlockItemProps extends NodeViewProps {
	videoRenderer?: VideoRenderer;
}

export function VideoBlockItem({ node, editor, videoRenderer }: VideoBlockItemProps) {
	/** Computed */
	const { videoId } = node.attrs as VideoBlockAttrs;

	/** Render */
	return (
		<NodeViewWrapper
			data-cy="video-block"
			className="video-block my-2"
		>
			{videoId && videoRenderer ? (
				videoRenderer({ videoId, isEditable: editor.isEditable })
			) : (
				<div className="card flex items-center gap-2 p-4 text-mauve-11">
					<Icon name="Video" />
					<span>{localized("videos.video")}</span>
				</div>
			)}
		</NodeViewWrapper>
	);
}
